import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaFileDownload, FaFolderOpen, FaListUl, FaExclamationTriangle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import inversionesExteriorService from '../../services/inversionesExteriorService';
import { ESTADOS_CARGA, conMiles, formatearFechaHora } from '../InversionesExterior/formatos';
import '../../styles/components/DJ1929InversionesExteriorTab.css';

// «Racional: 1 · Fintual: 2»
const cargasPorFuente = (cargas) => {
    const cuenta = {};
    for (const carga of cargas) cuenta[carga.fuente_nombre] = (cuenta[carga.fuente_nombre] ?? 0) + 1;
    return Object.entries(cuenta).map(([fuente, cantidad]) => `${fuente}: ${cantidad}`).join(' · ');
};

/**
 * Componente para DJ1929 - Inversiones y rentas en el exterior
 *
 * Muestra lo que va a la declaración (cargas validadas, sección C de control) y
 * permite descargar el CSV para el importador del SII. Los datos se cargan, revisan
 * y validan en el módulo Inversiones exterior; mientras quede una carga sin validar
 * no se descarga (D7).
 */
const DJ1929InversionesExteriorTab = ({ year, idCompany, rutEmpresa, token }) => {
    const [resumen, setResumen] = useState(null);
    const [descargando, setDescargando] = useState(false);

    useEffect(() => {
        const cargar = async () => {
            setResumen(null);
            try {
                setResumen(await inversionesExteriorService.getResumenDJ1929(idCompany, year, token));
            } catch (error) {
                Swal.fire({ icon: 'error', title: 'Error', text: error.message });
            }
        };
        cargar();
    }, [year, idCompany, token]);

    const handleDescargar = async () => {
        setDescargando(true);
        try {
            await inversionesExteriorService.descargarCSVDJ1929(idCompany, year, rutEmpresa, token);
            Swal.fire({
                icon: 'success',
                title: 'Descarga exitosa',
                text: `Súbelo en el importador del SII, formulario 1929 del AT ${year}.`
            });
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'No se generó el CSV', text: error.message });
        } finally {
            setDescargando(false);
        }
    };

    if (!resumen) {
        return <div className="dj1929-loading">Cargando datos...</div>;
    }

    const {
        anio_comercial: anioComercial, cargas_validadas: validadas, cargas_sin_validar: sinValidar,
        totales, columnas_monto: columnasMonto, filas_con_aviso: filasConAviso, puede_descargar: puedeDescargar, motivo
    } = resumen;

    return (
        <div className="dj1929-tab">
            <div className="dj1929-header">
                <h3>DJ 1929 - Inversiones y rentas en el exterior - AT {year} (año comercial {anioComercial})</h3>
                <p className="dj1929-subtitle">
                    Los datos se cargan, revisan y validan en <Link to="/inversiones-exterior">Inversiones exterior</Link>.
                    Aquí se genera el CSV para el importador del SII con las cargas validadas.
                </p>
            </div>

            <div className="dj1929-cards">
                <div className="dj1929-card">
                    <div className="dj1929-card-icon"><FaFolderOpen /></div>
                    <div className="dj1929-card-info">
                        <span className="dj1929-card-value">{validadas.length}</span>
                        <span className="dj1929-card-label">Cargas validadas</span>
                        {validadas.length > 0 && <span className="dj1929-card-detail">{cargasPorFuente(validadas)}</span>}
                    </div>
                </div>
                <div className="dj1929-card">
                    <div className="dj1929-card-icon dj1929-card-icon--green"><FaListUl /></div>
                    <div className="dj1929-card-info">
                        <span className="dj1929-card-value">{totales.casos}</span>
                        <span className="dj1929-card-label">Filas a declarar</span>
                        <span className="dj1929-card-detail">Solo las incluidas de cargas validadas</span>
                    </div>
                </div>
                <div className="dj1929-card">
                    <div className="dj1929-card-icon dj1929-card-icon--orange"><FaExclamationTriangle /></div>
                    <div className="dj1929-card-info">
                        <span className="dj1929-card-value">{filasConAviso}</span>
                        <span className="dj1929-card-label">Filas con avisos</span>
                        <span className="dj1929-card-detail">Las revisó el contador al validar</span>
                    </div>
                </div>
            </div>

            {sinValidar.length > 0 && (
                <div className="dj1929-pendientes">
                    <h4><FaExclamationTriangle /> Cargas sin validar</h4>
                    <p>
                        Mientras quede alguna, el CSV no se descarga. Valídalas o descártalas
                        en <Link to="/inversiones-exterior">Inversiones exterior</Link>.
                    </p>
                    <ul>
                        {sinValidar.map((carga) => (
                            <li key={carga.id}>
                                {carga.fuente_nombre} · {carga.nombre_archivo ?? 'ingreso manual'} · {ESTADOS_CARGA[carga.estado] ?? carga.estado}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {validadas.length > 0 && (
                <div className="dj1929-section">
                    <h4>Cargas validadas</h4>
                    <div className="dj1929-table-wrap">
                        <table className="dj1929-table">
                            <thead>
                                <tr>
                                    <th>Fuente</th>
                                    <th>Archivo</th>
                                    <th>Validada</th>
                                    <th className="text-right">Filas</th>
                                    <th className="text-right">Con avisos</th>
                                </tr>
                            </thead>
                            <tbody>
                                {validadas.map((carga) => (
                                    <tr key={carga.id}>
                                        <td>{carga.fuente_nombre}</td>
                                        <td>{carga.nombre_archivo ?? 'Ingreso manual'}</td>
                                        <td>{formatearFechaHora(carga.validado_en)}</td>
                                        <td className="text-right">{carga.filas}</td>
                                        <td className="text-right">{carga.filas_con_aviso}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {totales.casos > 0 && (
                <div className="dj1929-section">
                    <h4>Sección C · totales de control</h4>
                    <div className="dj1929-table-wrap">
                        <table className="dj1929-table">
                            <thead>
                                <tr>
                                    <th>Columna</th>
                                    <th className="text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Total de casos informados</td>
                                    <td className="text-right">{totales.casos}</td>
                                </tr>
                                {columnasMonto.map((columna) => (
                                    <tr key={columna.campo}>
                                        <td>{columna.titulo}</td>
                                        <td className="text-right">${conMiles(totales[columna.campo])}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="dj1929-actions">
                <button
                    className="dj1929-btn-csv"
                    onClick={handleDescargar}
                    disabled={descargando || !puedeDescargar}
                >
                    <FaFileDownload />
                    {descargando ? 'Generando...' : 'Descargar CSV DJ1929'}
                </button>
                {motivo && <p className="dj1929-warning">{motivo}</p>}
            </div>
        </div>
    );
};

export default DJ1929InversionesExteriorTab;
